import { css, cx, useTheme } from '@fable/theme'
import { FlexBox, Loader, LoaderType } from '@fable/components'
import { ChartYAxis } from './ChartYAxis'
import { ChartXAxis } from './ChartXAxis'
import { LineChart } from './LineChart'
import { ChartLegend } from '../../chart_legend'
import { useChartData } from '../hooks/useChartData'
import { useStatsTheme } from 'app/webview/pages/stats_profile/theme/ctx'
import { useMotionValueEvent, useScroll } from 'framer-motion'
import { useRef } from 'react'

interface Props {
  onScrollEnd?: () => void
  isFetching?: boolean
}

export const RatingComparisonChart = ({
  onScrollEnd,
  isFetching = false,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const { colors } = useTheme()
  const theme = useStatsTheme((s) => s.theme)
  const { data } = useChartData()
  const userRatings = data.length ? data[0] : null
  const totalRatings = userRatings?.data.length || 0
  const maxBooks = 8
  const remaining = totalRatings - maxBooks
  const extraSpacing = totalRatings > maxBooks ? remaining * 46 : 0
  const { scrollXProgress } = useScroll({ container: ref })

  useMotionValueEvent(scrollXProgress, 'change', (latest) => {
    if (latest === 1) {
      onScrollEnd && onScrollEnd()
    }
  })

  return (
    <div
      className={css`
        padding: 10px 0 10px;
        width: calc(100% + 32px);
        position: relative;
        left: -16px;
      `}
    >
      <div
        className={css`
          width: 100%;
          position: relative;
          overflow: hidden;
        `}
      >
        <ChartYAxis />
        <div
          ref={ref}
          className={css`
            width: 100%;
            padding: 0 24px 0 16px;
            display: flex;
            align-items: center;
            overflow-x: scroll;
            overflow-y: hidden;
            -ms-overflow-style: none;
            scrollbar-width: none;
            &::-webkit-scrollbar {
              display: none;
            }
          `}
        >
          <FlexBox
            className={css`
              width: calc(100% + ${extraSpacing}px);
              gap: 6px;
              flex-shrink: 0;
            `}
            flexDirection="column"
          >
            <div
              className={css`
                width: 100%;
                height: 180px;
              `}
            >
              <LineChart />
            </div>
            <ChartXAxis />
          </FlexBox>
          {isFetching ? (
            <FlexBox
              className={css`
                padding: 0 8px 48px;
                width: 100px;
                flex-shrink: 0;
                position: relative;
                z-index: 0;
              `}
            >
              <FlexBox
                className={css`
                  width: 100%;
                  height: 100%;
                  background-color: ${theme.colors.section.bgSecondary};
                  position: relative;
                  border-radius: 20px;
                `}
                centerAll
              >
                <Loader
                  type={LoaderType.contained}
                  className={css`
                    svg {
                      width: 40px !important;
                      height: 40px !important;
                    }
                  `}
                />
              </FlexBox>
            </FlexBox>
          ) : null}
        </div>
        <span
          className={cx(
            'anim__rating-cover',
            css`
              width: 100%;
              height: 190px;
              background-color: ${theme.colors.section.bg};
              position: absolute;
              top: 0;
              left: 0;
              z-index: 1;
              pointer-events: none;
              will-change: transform;
            `
          )}
        />
      </div>
      <ChartLegend
        className={css`
          padding: 20px 16px 0;
        `}
        items={[
          {
            color: theme.colors.primary,
            label: 'You',
          },
          {
            color: colors.uiPearl,
            label: 'The community',
          },
        ]}
      />
    </div>
  )
}
